import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button, Spinner, Table } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import YearlyCalendar from "../../calendar/YearlyCalendar/YearlyCalendar";
import AbsenceAdjustmentModal, { TARGET } from "../../Modal/AbsenceAdjustmentModal/AbsenceAdjustmentModal";
import {
  createAbsenceAdjustment,
  fetchAbsenceAdjustments,
  fetchHolidays,
  fetchUserAbsenceBalance,
} from "../../../redux/slices/absenceSlice";

const StatCard = ({ label, value }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body>
      <div className="txt-subtle small">{label}</div>
      <div className="fs-3 fw-bold txt-main">{value}</div>
    </Card.Body>
  </Card>
);

const formatSignedDays = (days) => (days > 0 ? `+${days}` : String(days));

const AbsencesTab = ({ userId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [year, setYear] = useState(() => new Date().getFullYear());
  const [loadFailed, setLoadFailed] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);

  const absences = useSelector((state) => state.absences?.items || []);
  const holidays = useSelector((state) => state.absences?.holidays || []);
  const storedBalance = useSelector((state) => state.absences?.userBalance);
  const storedAdjustments = useSelector((state) => state.absences?.userAdjustments);
  const balance =
    storedBalance && String(storedBalance.user_id) === String(userId) && storedBalance.year === year
      ? storedBalance
      : null;
  const adjustments =
    storedAdjustments && String(storedAdjustments.userId) === String(userId) ? storedAdjustments.items : [];

  useEffect(() => {
    setLoadFailed(false);
    dispatch(fetchUserAbsenceBalance({ userId, year })).then((action) => {
      if (fetchUserAbsenceBalance.rejected.match(action)) setLoadFailed(true);
    });
    dispatch(fetchHolidays({ year, userId }));
  }, [dispatch, userId, year]);

  useEffect(() => {
    dispatch(fetchAbsenceAdjustments({ userId }));
  }, [dispatch, userId]);

  // Rejects with the API error so the modal can show it.
  const handleCreateAdjustment = async (adjustment) => {
    const action = await dispatch(createAbsenceAdjustment({ userId, ...adjustment }));
    if (createAbsenceAdjustment.rejected.match(action)) throw action.payload;
    dispatch(fetchUserAbsenceBalance({ userId, year }));
  };

  const renderBalance = () => {
    if (loadFailed) {
      return <p className="txt-subtle">{t("components.employeeProfile.absencesTab.loadError")}</p>;
    }
    if (!balance) {
      return <Spinner animation="border" size="sm" role="status" />;
    }

    return (
      <Row className="g-3">
        <Col md={4}>
          <StatCard label={t("components.employeeProfile.absencesTab.acquiredDays")} value={balance.total_days} />
        </Col>
        <Col md={4}>
          <StatCard label={t("components.employeeProfile.absencesTab.usedDays")} value={balance.used_days} />
        </Col>
        <Col md={4}>
          <StatCard label={t("components.employeeProfile.absencesTab.availableDays")} value={balance.available_days} />
        </Col>
      </Row>
    );
  };

  const renderAdjustments = () => {
    if (adjustments.length === 0) {
      return <p className="txt-subtle mb-0">{t("components.employeeProfile.absencesTab.adjustment.empty")}</p>;
    }

    return (
      <Table responsive size="sm" className="mb-0 align-middle">
        <thead>
          <tr>
            <th className="txt-subtle fw-semibold">{t("components.employeeProfile.absencesTab.adjustment.date")}</th>
            <th className="txt-subtle fw-semibold">{t("components.employeeProfile.absencesTab.adjustment.cycleYear")}</th>
            <th className="txt-subtle fw-semibold">{t("components.employeeProfile.absencesTab.adjustment.target")}</th>
            <th className="txt-subtle fw-semibold">{t("components.employeeProfile.absencesTab.adjustment.days")}</th>
            <th className="txt-subtle fw-semibold">{t("components.employeeProfile.absencesTab.adjustment.reason")}</th>
            <th className="txt-subtle fw-semibold">{t("components.employeeProfile.absencesTab.adjustment.createdBy")}</th>
          </tr>
        </thead>
        <tbody>
          {adjustments.map((adjustment) => (
            <tr key={adjustment.id}>
              <td>{adjustment.created_at ? new Date(adjustment.created_at).toLocaleDateString("pt-PT") : "-"}</td>
              <td>{adjustment.year}</td>
              <td>
                {adjustment.target === TARGET.ACQUIRED
                  ? t("components.employeeProfile.absencesTab.acquiredDays")
                  : t("components.employeeProfile.absencesTab.availableDays")}
              </td>
              <td className={`fw-semibold ${adjustment.days < 0 ? "txt-danger" : "text-success"}`}>
                {formatSignedDays(adjustment.days)}
              </td>
              <td>{adjustment.reason || "-"}</td>
              <td>{adjustment.created_by_name || "-"}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <div className="py-4">
      <h5 className="fw-bold mb-3">
        {t("components.employeeProfile.absencesTab.title")} {year}
      </h5>
      {renderBalance()}

      <Card className="border-0 shadow-sm mt-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">{t("components.employeeProfile.absencesTab.adjustment.title")}</h6>
            {balance?.can_edit && (
              <Button size="sm" onClick={() => setShowAdjustmentModal(true)}>
                <FaPlus className="me-1" />
                {t("components.employeeProfile.absencesTab.adjustment.addButton")}
              </Button>
            )}
          </div>
          {renderAdjustments()}
        </Card.Body>
      </Card>

      <h5 className="fw-bold mt-5 mb-3">{t("components.employeeProfile.absencesTab.calendarTitle")}</h5>
      <YearlyCalendar
        absences={absences}
        user={{ id: userId }}
        holidays={holidays}
        currentYear={year}
        onYearChange={setYear}
      />

      <AbsenceAdjustmentModal
        show={showAdjustmentModal}
        onClose={() => setShowAdjustmentModal(false)}
        onSubmit={handleCreateAdjustment}
      />
    </div>
  );
};

export default AbsencesTab;
